import type {
  EntityDetailSchema,
  ConfigEntityDetail,
  ConfigEntity,
  DomainEntity,
  EnvironmentKey,
  UserInfo,
} from '@web-config-app/core';
import {
  EntityStatusValue,
  isConfigApiError,
  topologicalSortEntities,
  buildReverseGraph,
  getCascadingFailureIdsFromReverseGraph,
  applySchemaDefaultsToEntity,
  getConfigEntitySchemaWithDomainEntityAttributes,
} from '@web-config-app/core';
import { captureError } from '@leagueplatform/observability';
import { isProduction } from '@leagueplatform/app-environment';
import type { EntityWithDependencies } from '../../utilities/get-all-referenced-entities/get-all-referenced-entities';
import { publishEntity } from '../../utilities/publish-entity/publish-entity';
import { withBestEffortReporting } from '../../bulk-action-reporting/with-best-effort-reporting';
import { buildOperationErrorDetail } from '../../utilities/build-operation-error-detail/build-operation-error-detail';
import type { BulkActionReportingStore } from '../../bulk-action-reporting/bulk-action-reporting.store';
import { getEntityMetadata } from './discover-deep-publish-tree';

const DEEP_PUBLISH_DELAY_MS = 1000;

const isPublishableOnFirstRun = (status?: string) =>
  status !== EntityStatusValue.Published &&
  status !== EntityStatusValue.Archived;

/**
 * Abort-aware delay that resolves immediately when the signal fires.
 */
const abortableDelay = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });

export interface ExecuteDeepPublishParams {
  entitiesMap: Map<string, EntityWithDependencies>;
  schemasMap: Map<string, EntityDetailSchema>;
  previouslyFailedIds: Set<string>;
  configEntity: ConfigEntity | DomainEntity;
  /**
   * The base config-entity envelope schema before any domain-specific
   * attributes are spliced in. Used together with `schemasMap` to build
   * per-entity-type config entities so that `prepareEntityBodyData` runs
   * against the correct schema for each entity in the tree — not just the
   * root entity's schema.
   */
  baseConfigEntitySchema: EntityDetailSchema;
  domainEntityType: string;
  environment: EnvironmentKey;
  environmentFetch: (path: string, options?: RequestInit) => Promise<Response>;
  fetchEntityOnSourceById: (
    configEntityId: string,
    entityType?: string,
  ) => Promise<{ data?: unknown }>;
  userInfo?: UserInfo;
  formatMessage: (descriptor: { id: string }) => string;
  bulkActionReportingStore: BulkActionReportingStore;
  abortSignal: AbortSignal;
}

export interface ExecuteDeepPublishResult {
  failedEntityIds: Set<string>;
}

/**
 * Executes the deep publish loop: sorts entities topologically, publishes
 * them sequentially, and handles cascading failures and retry logic.
 *
 * This is a standalone async function (not a hook) so it can be tested
 * and reasoned about independently of React state management.
 */
export async function executeDeepPublishLoop({
  entitiesMap,
  schemasMap,
  previouslyFailedIds,
  configEntity,
  baseConfigEntitySchema,
  domainEntityType,
  environment,
  environmentFetch,
  fetchEntityOnSourceById,
  userInfo,
  formatMessage,
  bulkActionReportingStore,
  abortSignal,
}: ExecuteDeepPublishParams): Promise<ExecuteDeepPublishResult> {
  const isRetry = previouslyFailedIds.size > 0;

  // Build a per-entity-type config entity so that `prepareEntityBodyData`
  // uses the correct schema for each entity type in the tree (activities,
  // campaigns, groups, etc.) rather than always using the root entity's
  // schema. The schema mismatch caused fields like `completionCriteria`
  // to be stripped during the sanitisation pipeline.
  const configEntityByType = new Map<string, ConfigEntity | DomainEntity>();
  const getConfigEntityForType = (
    entityType: string,
  ): ConfigEntity | DomainEntity => {
    const cached = configEntityByType.get(entityType);
    if (cached) return cached;

    const entitySchema = schemasMap.get(entityType);
    if (
      !entitySchema?.properties?.attributes ||
      !('domainEntity' in configEntity)
    ) {
      return configEntity;
    }

    const typed: ConfigEntity = {
      ...configEntity,
      schema: getConfigEntitySchemaWithDomainEntityAttributes(
        baseConfigEntitySchema,
        entitySchema,
      ),
      domainEntity: {
        ...configEntity.domainEntity,
        schema: entitySchema,
      },
    };
    configEntityByType.set(entityType, typed);
    return typed;
  };

  // Build items for topological sorting
  const sortableItems = Array.from(entitiesMap.values()).map(
    ({ entity: e, dependencies }) => ({
      id: e.id,
      dependencies,
      entity: e,
    }),
  );

  // Sort dependencies-first (bottom-up)
  const orderedItems = topologicalSortEntities(sortableItems);

  const archivedItems = isRetry
    ? []
    : orderedItems.filter((item) => {
        const status = item.entity.attributes?.entityMetadata?.status;
        return status === EntityStatusValue.Archived;
      });

  // Filter to entities that should actually enter the publish path this run.
  let itemsToPublish = orderedItems.filter((item) => {
    if (isRetry) {
      return previouslyFailedIds.has(item.entity.id);
    }
    const status = item.entity.attributes?.entityMetadata?.status;
    return isPublishableOnFirstRun(status);
  });
  const itemIdsToPublish = new Set(
    itemsToPublish.map((item) => item.entity.id),
  );

  const store = bulkActionReportingStore.getState();
  if (isRetry && store.session) {
    const { retriedEntityIds } = store.resumeSessionForRetry();
    if (retriedEntityIds.length === 0) {
      return { failedEntityIds: new Set() };
    }
    // Narrow the publish list to only the actual error rows from the session
    // store, excluding cascade-skipped entities that were marked as failed
    // but weren't true errors.
    const retriedSet = new Set(retriedEntityIds);
    itemsToPublish = itemsToPublish.filter((item) =>
      retriedSet.has(item.entity.id),
    );
  } else {
    store.createSession({
      operationType: 'publish',
      domainEntityType,
      totalItems: orderedItems.length,
      sourceEnvironment: environment,
    });

    // Record skipped items (not being published in this run)
    orderedItems
      .filter((item) => !itemIdsToPublish.has(item.entity.id))
      .forEach((item) => {
        const metadata = getEntityMetadata(item.entity);

        withBestEffortReporting(() =>
          store.recordItemResult({
            sourceEntityId: item.entity.id,
            domainEntityId: metadata.domainEntityId,
            entityType: metadata.domainEntityType,
            entityName: metadata.entityName,
            status: 'skipped',
          }),
        );
      });

    // Pre-register all items to publish as loading so they appear
    // in the reporting modal immediately (streaming pattern).
    itemsToPublish.forEach((item) => {
      const metadata = getEntityMetadata(item.entity);

      withBestEffortReporting(() =>
        store.recordItemResult({
          sourceEntityId: item.entity.id,
          domainEntityId: metadata.domainEntityId,
          entityType: metadata.domainEntityType,
          entityName: metadata.entityName,
          status: 'loading',
        }),
      );
    });
  }

  // Build dependency graph and its reverse for cascading failure lookups.
  // The reverse graph is built once here rather than inside each
  // getCascadingFailureIds call to avoid O(E) work per failure.
  const dependencyGraph = new Map<string, string[]>();
  Array.from(entitiesMap.values()).forEach(({ entity: e, dependencies }) => {
    dependencyGraph.set(
      e.id,
      dependencies.map((d) => d.id),
    );
  });
  const reverseGraph = buildReverseGraph(dependencyGraph);

  // Track failed entities for cascading skips
  const failedEntityIds = new Set<string>();

  if (!isRetry) {
    archivedItems.forEach((item) => {
      const itemMetadata = getEntityMetadata(item.entity);

      withBestEffortReporting(() =>
        store.recordItemResult({
          sourceEntityId: item.entity.id,
          domainEntityId: itemMetadata.domainEntityId,
          entityType: itemMetadata.domainEntityType,
          entityName: itemMetadata.entityName,
          status: 'error',
          error: {
            detail: formatMessage({
              id: 'DEEP_PUBLISH_ARCHIVED_ENTITY_FAILURE',
            }),
          },
        }),
      );

      failedEntityIds.add(item.entity.id);
      const cascadingFailureIds = getCascadingFailureIdsFromReverseGraph(
        item.entity.id,
        reverseGraph,
      );
      cascadingFailureIds.forEach((cascadingFailureId) => {
        failedEntityIds.add(cascadingFailureId);
      });
    });
  }

  // Process entities in topological order
  const lastPublishIndex = itemsToPublish.length - 1;

  // eslint-disable-next-line no-restricted-syntax -- Sequential async publish loop requires for...of for await, break, and continue
  for (const [publishIndex, item] of itemsToPublish.entries()) {
    if (abortSignal.aborted) {
      break;
    }

    const itemMetadata = getEntityMetadata(item.entity);

    // Check if this entity should be failed due to cascading failure
    if (failedEntityIds.has(item.entity.id)) {
      withBestEffortReporting(() =>
        store.recordItemResult({
          sourceEntityId: item.entity.id,
          domainEntityId: itemMetadata.domainEntityId,
          entityType: itemMetadata.domainEntityType,
          entityName: itemMetadata.entityName,
          status: 'error',
          error: {
            detail: formatMessage({
              id: 'DEEP_PUBLISH_DEPENDENCY_FAILURE_SKIP',
            }),
          },
        }),
      );
      // eslint-disable-next-line no-continue -- Cascading failure skip within sequential async loop
      continue;
    }

    try {
      // Always do a fresh GET to ensure we have the latest data
      // eslint-disable-next-line no-await-in-loop -- Intentional: must fetch latest data before each sequential publish
      const freshResponse = await fetchEntityOnSourceById(
        item.entity.id,
        itemMetadata.domainEntityType,
      );
      // fetchEntityOnSourceById returns an untyped response; the data shape
      // is always ConfigEntityDetail when the fetch succeeds.
      const freshEntity =
        (freshResponse?.data as ConfigEntityDetail | undefined) ?? item.entity;
      const freshMetadata = getEntityMetadata(freshEntity);

      // Diagnostic: pinpoint where `completionData.completionCriteria` is lost
      // for the deep-publish-of-generic-event-activity case. Reads the shape
      // directly rather than going through the guard's `getCompletionData`,
      // so it cannot share a defect the guard's own path lookup might have.
      if (freshMetadata.domainEntityType === 'config-activity') {
        const completionData =
          freshEntity.attributes?.domainEntityAttributes?.completionData;
        if (
          completionData?.eventId === 'task_completed' &&
          !completionData?.completionCriteria
        ) {
          captureError(
            new Error(
              'Deep publish observed a config-activity with generic completion event and no completionCriteria after the fresh GET',
            ),
            {
              errorName: 'DeepPublishMissingCompletionCriteriaDebug',
              context: {
                DeepPublishMissingCompletionCriteria: {
                  stage: 'post-fetch',
                  entityId: item.entity.id,
                  domainEntityType: freshMetadata.domainEntityType,
                  completionDataPreview: JSON.stringify(completionData)?.slice(
                    0,
                    500,
                  ),
                },
              },
            },
          );
        }
      }

      if (freshMetadata.status === EntityStatusValue.Archived) {
        throw new Error(
          formatMessage({
            id: 'DEEP_PUBLISH_ARCHIVED_ENTITY_FAILURE',
          }),
        );
      }

      // Apply schema defaults before publishing
      const entitySchema = schemasMap.get(itemMetadata.domainEntityType);
      const entityDataWithDefaults = entitySchema
        ? applySchemaDefaultsToEntity(freshEntity, entitySchema)
        : freshEntity;

      // Attempt publish — use the per-entity-type config entity so that
      // `prepareEntityBodyData` sanitises the data against the correct
      // schema (e.g. activity schema for activities, not the campaign schema).
      // eslint-disable-next-line no-await-in-loop -- Intentional: entities must be published sequentially (dependency order)
      await publishEntity({
        entity: getConfigEntityForType(freshMetadata.domainEntityType),
        instanceId: item.entity.id,
        data: entityDataWithDefaults,
        fetchFn: environmentFetch,
        userInfo,
        status: freshEntity.attributes?.entityMetadata?.status,
        domainEntityType: itemMetadata.domainEntityType,
      });

      withBestEffortReporting(() =>
        store.recordItemResult({
          sourceEntityId: item.entity.id,
          domainEntityId: itemMetadata.domainEntityId,
          entityType: itemMetadata.domainEntityType,
          entityName: itemMetadata.entityName,
          status: 'success',
        }),
      );

      // Pace between publish calls to avoid overwhelming the backend,
      // but skip the delay after the last item.
      if (publishIndex < lastPublishIndex) {
        // eslint-disable-next-line no-await-in-loop -- Intentional: sequential publishes with pacing
        await abortableDelay(DEEP_PUBLISH_DELAY_MS, abortSignal);
      }
    } catch (error) {
      failedEntityIds.add(item.entity.id);

      const errorDetail = isConfigApiError(error)
        ? buildOperationErrorDetail(error, formatMessage)
        : {
            detail:
              error instanceof Error ? error.message : 'Unknown publish error',
          };

      withBestEffortReporting(() =>
        store.recordItemResult({
          sourceEntityId: item.entity.id,
          domainEntityId: itemMetadata.domainEntityId,
          entityType: itemMetadata.domainEntityType,
          entityName: itemMetadata.entityName,
          status: 'error',
          error: errorDetail,
        }),
      );

      // Apply cascading failure — mark all transitive dependents as failed
      // so they are skipped when encountered later in the loop
      const cascadedIds = getCascadingFailureIdsFromReverseGraph(
        item.entity.id,
        reverseGraph,
      );
      cascadedIds.forEach((cascadedId) => {
        failedEntityIds.add(cascadedId);
      });

      if (isProduction()) {
        captureError(
          error instanceof Error ? error : new Error(String(error)),
          {
            errorName: 'DeepPublishEntityError',
            context: {
              DeepPublishEntity: {
                entityId: item.entity.id,
                domainEntityType: itemMetadata.domainEntityType,
              },
            },
          },
        );
      }
    }
  }

  if (!abortSignal.aborted) {
    store.completeSession();
  }

  return { failedEntityIds };
}
